import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { matches } from "@/mocks/matches";
import type { MatchStats } from "@/mocks/matches";
import { teams } from "@/mocks/teams";

const eventIcon: Record<string, { icon: string; className: string }> = {
  goal: { icon: "ri-football-fill", className: "bg-accent-500 text-background-50" },
  yellow: { icon: "ri-bookmark-fill", className: "bg-yellow-400 text-foreground-950" },
  red: { icon: "ri-bookmark-fill", className: "bg-red-500 text-background-50" },
  sub: { icon: "ri-repeat-line", className: "bg-secondary-500 text-background-50" },
  var: { icon: "ri-tv-line", className: "bg-primary-500 text-background-50" },
  kickoff: { icon: "ri-flag-line", className: "bg-background-300 text-foreground-800" },
  half: { icon: "ri-pause-circle-line", className: "bg-background-300 text-foreground-800" },
  end: { icon: "ri-stop-circle-line", className: "bg-background-300 text-foreground-800" },
};

const statMeta: { key: keyof MatchStats; label: string }[] = [
  { key: "possession", label: "控球率" },
  { key: "shots", label: "射门" },
  { key: "shotsOnTarget", label: "射正" },
  { key: "corners", label: "角球" },
  { key: "fouls", label: "犯规" },
  { key: "offsides", label: "越位" },
  { key: "passes", label: "传球次数" },
];

export default function MatchDetail() {
  const { matchId } = useParams<{ matchId: string }>();
  const match = matches.find((m) => m.id === matchId);
  const [openEvent, setOpenEvent] = useState<number | null>(0);

  if (!match) {
    return (
      <div className="w-full px-4 md:px-6 pt-32 pb-20 min-h-[70vh] flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-error-warning-line" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
          未找到该场比赛
        </h1>
        <p className="mt-2 text-sm text-foreground-600">比赛信息可能已更新或链接有误。</p>
        <Link
          to="/category/season"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
        >
          浏览赛事分类
        </Link>
      </div>
    );
  }

  const home = teams.find((t) => t.id === match.homeTeamId);
  const away = teams.find((t) => t.id === match.awayTeamId);
  const hasScore = match.homeScore !== null && match.awayScore !== null;

  const related = matches.filter((m) => m.id !== match.id && m.season === match.season).slice(0, 3);

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={match.cover}
            alt={`${home?.name} vs ${away?.name} 比赛详情`}
            title={`${home?.name} vs ${away?.name} 欧冠比赛详情`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/70 to-primary-950/95"></div>
        </div>

        <div className="relative w-full px-4 md:px-6 pt-24 md:pt-28 pb-10 md:pb-14">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300 mb-5">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <Link to="/category/season" className="hover:text-accent-400 transition-colors">
              赛事分类
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <span className="text-background-100">比赛详情</span>
          </nav>

          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-background-100">
              {match.season} 赛季
            </span>
            <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              {match.stage}
            </span>
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-background-100">
              {match.round}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 max-w-4xl">
            <div className="flex md:flex-col items-center gap-3 md:gap-2 md:text-center">
              <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-background-50 flex items-center justify-center p-2.5 overflow-hidden">
                <img
                  src={home?.crest}
                  alt={`${home?.name}队徽`}
                  title={`${home?.name} 欧冠比赛详情`}
                  className="w-full h-full object-contain"
                />
              </span>
              <span className="font-heading text-lg md:text-xl font-semibold text-background-50">
                {home?.name}
              </span>
            </div>

            <div className="text-center order-first md:order-none">
              {hasScore ? (
                <div className="font-heading text-4xl md:text-6xl font-bold text-background-50">
                  {match.homeScore}
                  <span className="mx-2 text-background-400">:</span>
                  {match.awayScore}
                </div>
              ) : (
                <div className="font-heading text-3xl md:text-4xl font-bold text-background-50">
                  VS
                </div>
              )}
              <p className="mt-2 text-xs md:text-sm text-background-200">
                {match.date.replace(/-/g, "/")} {match.time}
              </p>
              {match.status === "live" && (
                <Link
                  to={`/live/${match.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-live-line" aria-hidden="true"></i>
                  进入直播
                </Link>
              )}
            </div>

            <div className="flex md:flex-col items-center gap-3 md:gap-2 md:text-center">
              <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-background-50 flex items-center justify-center p-2.5 overflow-hidden">
                <img
                  src={away?.crest}
                  alt={`${away?.name}队徽`}
                  title={`${away?.name} 欧冠比赛详情`}
                  className="w-full h-full object-contain"
                />
              </span>
              <span className="font-heading text-lg md:text-xl font-semibold text-background-50">
                {away?.name}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: "ri-calendar-line", label: "比赛时间", value: `${match.date} ${match.time}` },
            { icon: "ri-map-pin-line", label: "比赛场地", value: match.venue },
            { icon: "ri-trophy-line", label: "赛事阶段", value: `${match.stage} · ${match.round}` },
            { icon: "ri-user-star-line", label: "主裁判", value: match.referee },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-background-200 bg-background-100 p-4 flex items-start gap-3"
            >
              <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-lg">
                <i className={item.icon} aria-hidden="true"></i>
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">{item.label}</p>
                <p className="text-sm font-medium text-foreground-900 mt-0.5 break-words">
                  {item.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-5">
                比赛事件时间轴
              </h2>
              {match.events.length === 0 ? (
                <div className="rounded-md border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
                  比赛尚未开始，暂无赛况与事件，以下为赛前信息与展望。
                </div>
              ) : (
                <ol className="relative border-l border-background-200 ml-4">
                  {match.events.map((e, i) => {
                    const meta = eventIcon[e.type] ?? eventIcon.kickoff;
                    const open = openEvent === i;
                    return (
                      <li key={`${e.minute}-${i}`} className="relative pl-8 pb-5 last:pb-0">
                        <span
                          className={`absolute -left-[17px] top-0 w-8 h-8 flex items-center justify-center rounded-full text-sm ${meta.className}`}
                        >
                          <i className={meta.icon} aria-hidden="true"></i>
                        </span>
                        <button
                          type="button"
                          onClick={() => setOpenEvent(open ? null : i)}
                          className="w-full text-left rounded-md px-2 py-1 -ml-2 hover:bg-background-100 transition-colors"
                        >
                          <span className="flex items-center gap-2 flex-wrap">
                            <span className="font-heading text-sm font-bold text-foreground-950">
                              {e.minute}&apos;
                            </span>
                            <span className="text-sm font-semibold text-foreground-900">
                              {e.player}
                            </span>
                            {e.team !== "none" && (
                              <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] text-foreground-600">
                                {e.team === "home" ? home?.name : away?.name}
                              </span>
                            )}
                            <i
                              className={`ri-arrow-down-s-line ml-auto text-foreground-400 transition-transform ${
                                open ? "rotate-180" : ""
                              }`}
                              aria-hidden="true"
                            ></i>
                          </span>
                        </button>
                        {open && (
                          <p className="mt-2 ml-0 text-sm text-foreground-600 leading-relaxed animate-fade-in">
                            {e.detail}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {match.stats && (
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-5">
                  赛后数据统计
                </h2>
                <div className="flex flex-col gap-4">
                  {statMeta.map(({ key, label }) => {
                    const pair = match.stats![key];
                    const total = pair[0] + pair[1] || 1;
                    const leftPct = Math.round((pair[0] / total) * 100);
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="font-semibold text-foreground-900 w-10">{pair[0]}</span>
                          <span className="text-xs text-foreground-500">{label}</span>
                          <span className="font-semibold text-foreground-900 w-10 text-right">
                            {pair[1]}
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-background-200 overflow-hidden flex">
                          <div className="h-full bg-primary-500" style={{ width: `${leftPct}%` }}></div>
                          <div className="h-full bg-accent-500" style={{ width: `${100 - leftPct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-5 flex flex-col gap-6">
            {[home, away].map((team) =>
              team ? (
                <div
                  key={team.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center p-2 overflow-hidden shrink-0">
                      <img
                        src={team.crest}
                        alt={`${team.name}队徽`}
                        title={`${team.name} 欧冠球队`}
                        className="w-full h-full object-contain"
                      />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-foreground-950">
                        {team.name}
                      </h3>
                      <p className="text-xs text-foreground-500">
                        {team.englishName} · {team.country}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3">
                    {team.summary}
                  </p>
                  <div className="mt-4 pt-3 border-t border-background-200">
                    <p className="text-xs font-semibold text-foreground-700 mb-2">主要阵容</p>
                    <ul className="flex flex-wrap gap-1.5">
                      {team.players.slice(0, 4).map((p) => (
                        <li
                          key={p.id}
                          className="rounded-full bg-background-100 px-2.5 py-0.5 text-[11px] text-foreground-700"
                        >
                          {p.number} {p.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    to={`/team/${team.id}`}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    查看球队详情
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              ) : null,
            )}

            {!match.stats && (
              <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  赛前展望
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{match.preview}</p>
                <p className="mt-3 text-xs text-foreground-500">
                  比赛尚未开始，暂无赛后数据统计，赛后将第一时间更新。
                </p>
              </div>
            )}

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  相关推荐
                </h3>
                <Link
                  to={`/schedule/${match.season}`}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  完整赛程
                </Link>
              </div>
              <div className="flex flex-col gap-3">
                {related.map((m) => {
                  const h = teams.find((t) => t.id === m.homeTeamId);
                  const a = teams.find((t) => t.id === m.awayTeamId);
                  return (
                    <Link
                      key={m.id}
                      to={`/detail/${m.id}`}
                      className="flex items-center gap-3 rounded-md border border-background-200 p-3 hover:border-primary-300 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">
                          {h?.name} vs {a?.name}
                        </p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {m.date.slice(5).replace("-", "/")} {m.time} · {m.stage}
                        </p>
                      </div>
                      <span className="shrink-0 font-heading text-sm font-bold text-foreground-950">
                        {m.homeScore !== null ? `${m.homeScore}:${m.awayScore}` : "VS"}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}